import { useEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { ChevronUp, ChevronDown } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useIntercomLauncherVisibility } from "@/context/IntercomLauncherVisibilityContext";
import { carDetailsLabels } from "@/i18n";
import { useNativePlatform } from "@/hooks/useNativePlatform";

import { useIsNativeTablet } from "@/hooks/useIsNativeTablet";
import { useLandscapeOrientation } from "@/hooks/useLandscapeOrientation";
import { cn } from "@/lib/utils";

interface MobileBiddingBarProps {
  children: React.ReactNode;
  collapsedContent?: React.ReactNode;
  /**
   * Stick to the bottom of a parent panel (e.g. För dig side sheet)
   * instead of fixed full-viewport / body portal.
   */
  contained?: boolean;
}

const MIN_HEIGHT_NATIVE = 96;
const MIN_HEIGHT_DEFAULT = 80;

const MobileBiddingBar = ({
  children,
  collapsedContent,
  contained = false,
}: MobileBiddingBarProps) => {
  const { language } = useLanguage();
  const { setSuppressIntercomLauncher } = useIntercomLauncherVisibility();
  const carDetails = carDetailsLabels(language) as {
    bidding?: { close?: string; showMore?: string };
    vehicleInfo?: { showMore?: string };
  };
  const bi = {
    close: carDetails.bidding?.close ?? "Close",
    showMore:
      carDetails.bidding?.showMore ??
      carDetails.vehicleInfo?.showMore ??
      "Show more",
  };
  const [expanded, setExpanded] = useState(false);
  const startY = useRef<number | null>(null);
  const { isNative } = useNativePlatform();
  const isNativeTablet = useIsNativeTablet();
  const isLandscape = useLandscapeOrientation();
  const minHeight =
    isNativeTablet && isLandscape
      ? 56
      : contained
        ? 88
        : isNative
          ? MIN_HEIGHT_NATIVE
          : MIN_HEIGHT_DEFAULT;

  // Dölj Fin AI / Intercom-launchern medan mobil budpanel visas (undvik överlapp med budknapp)
  useEffect(() => {
    setSuppressIntercomLauncher(true);
    return () => setSuppressIntercomLauncher(false);
  }, [setSuppressIntercomLauncher]);

  /** Only the chevron strip toggles expand — not scrollable content or nested sheets. */
  const handleHandleTouchStart = (e: React.TouchEvent) => {
    startY.current = e.touches[0].clientY;
  };

  const handleHandleTouchEnd = (e: React.TouchEvent) => {
    if (startY.current === null) return;
    const endY = e.changedTouches[0].clientY;
    if (startY.current - endY > 40) setExpanded(true); // drag uppåt
    if (endY - startY.current > 40) setExpanded(false); // drag nedåt
    startY.current = null;
  };

  const collapsedBarStyle: CSSProperties =
    isNative && !expanded
      ? {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          minHeight: `${minHeight}px`,
          height: "auto",
          maxHeight: "none",
          overflow: "visible",
          // Collapsed: catch drag on handle only; expanded content needs pan-y scroll
          touchAction: "none",
          zIndex: 85,
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }
      : {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          minHeight: expanded ? "40vh" : `${minHeight}px`,
          maxHeight: expanded ? "80vh" : `${minHeight}px`,
          overflow: "hidden",
          touchAction: expanded ? "pan-y" : "none",
          zIndex: isNative ? 85 : 95,
          paddingBottom: isNative
            ? "env(safe-area-inset-bottom, 0px)"
            : undefined,
        };

  const bar = (
    <div
      className={cn(
        "transition-all duration-300 bg-secondary shadow-lg",
        contained ? "relative w-full shrink-0 border-t border-border/40" : "fixed bottom-0 left-0 right-0",
      )}
      style={
        contained
          ? {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              maxHeight: expanded ? "min(70vh, 560px)" : undefined,
              overflow: expanded ? "hidden" : "visible",
              zIndex: 20,
              // Parent overlay wrapper owns safe-area pad (avoid double white gap)
            }
          : collapsedBarStyle
      }
    >
      <button
        type="button"
        className="w-full flex items-center justify-center py-2 sm:py-1.5 bg-transparent touch-manipulation cursor-pointer"
        onClick={() => setExpanded((e) => !e)}
        onTouchStart={handleHandleTouchStart}
        onTouchEnd={handleHandleTouchEnd}
        aria-expanded={expanded}
        aria-label={expanded ? bi.close : bi.showMore}
      >
        {expanded ? (
          <ChevronDown className="w-5 h-5" />
        ) : (
          <ChevronUp className="w-5 h-5" />
        )}
      </button>
      {!expanded && collapsedContent && (
        <div
          className={cn(
            "px-4 pr-16 flex flex-col items-center justify-center",
            isNative && !contained ? "native-mobile-bidding-collapsed" : "pb-3",
          )}
        >
          {collapsedContent}
        </div>
      )}
      {expanded && (
        <div
          className="overflow-y-auto overscroll-contain"
          style={{
            maxHeight: contained ? "calc(min(70vh, 560px) - 48px)" : "calc(80vh - 60px)",
            WebkitOverflowScrolling: "touch",
            touchAction: "pan-y",
          }}
          onTouchStart={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      )}
    </div>
  );

  if (!contained && isNative && typeof document !== "undefined") {
    return createPortal(bar, document.body);
  }

  return bar;
};

export default MobileBiddingBar;
